import { BrowserStorage, SessionStorage } from "./BrowserStorage";

let IDPConnectService = {};

const PKCE_UNRESERVED =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";

/**
 * True when iaa is rendered inside an iframe (cross-origin checks may throw,
 * which itself implies cross-origin embedding).
 */
function isInIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

/**
 * iaa is hosted by wallet-webui inside an iframe only on the web platform.
 * Native (iOS/Android) opens iaa directly in a system browser/Custom Tab and
 * must continue to drive the full OAuth redirect flow itself.
 */
function isWalletWebIframe() {
  return window.PLATFORM === "web" && isInIframe();
}

function getWalletBasePath() {
  const path = (window.config && window.config.wallet_path) || "/wallet/";
  return `/${path.replace(/^\/+|\/+$/g, "")}/`.replace(/^\/\/$/, "/");
}

function getAuthBaseUrl() {
  return window.config.url && /^https?:\/\//i.test(window.config.url)
    ? window.config.url.replace(/\/$/, "")
    : `https://${(window.config.url || "").replace(/^\/+|\/+$/g, "")}`;
}

/**
 * Resolves the targetOrigin used when posting the IDP selection to the
 * wallet-webui parent. Order of preference:
 *   1. window.config.parent_origin (deployed override; tightest)
 *   2. document.referrer origin (parent URL when iframe is loaded cross-origin)
 *   3. "*" (the message carries no secrets, only the chosen IDP id and view name;
 *      the receiver validates event.origin against the iaa host)
 */
function getPostMessageTargetOrigin() {
  const parentOrigin = window.config && window.config.parent_origin;
  if (parentOrigin && /^https?:\/\//i.test(parentOrigin)) {
    try {
      return new URL(parentOrigin).origin;
    } catch (e) {
      /* fall through */
    }
  }
  if (document.referrer) {
    try {
      return new URL(document.referrer).origin;
    } catch (e) {
      /* fall through */
    }
  }
  return "*";
}

/**
 * Generates a cryptographically random string from the RFC 7636 unreserved set
 * (A-Z / a-z / 0-9 / "-" / "." / "_" / "~"). Used for `state`, `code_verifier`,
 * and `nonce`. RFC 7636 section 4.1 and RFC 6819 section 5.1.4.2.2 require a
 * CSPRNG; Math.random() is not sufficient.
 */
function generateRandomString(length) {
  const randomBytes = new Uint8Array(length);
  crypto.getRandomValues(randomBytes);
  let result = "";
  for (let i = 0; i < length; i++) {
    result += PKCE_UNRESERVED.charAt(randomBytes[i] % PKCE_UNRESERVED.length);
  }
  return result;
}

/**
 * Generates SHA-256 hash for code_challenge using subtle crypto
 */
async function generateCodeChallenge(codeVerifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);

  let hashArray = Array.from(new Uint8Array(hashBuffer));
  let hashString = String.fromCharCode.apply(null, hashArray);
  let base64 = btoa(hashString);

  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Standard OIDC Authorization Code Flow Initializer
 *
 * @param idp The idp object string/id
 * @param after ... what to do on return?
 *
 * @return Promise with authorize URL for redirect
 */
IDPConnectService.LoginToIdp = function (idp, after, last_view) {
  return new Promise(async (resolve, reject) => {
    try {
      // Web-only handoff: when iaa is embedded as the wallet-webui login UI,
      // the parent shell is the OAuth client (RFC 6749 §3.1.2 redirect_uri is
      // registered against the parent's /idp-callback). Forward the IDP
      // selection up and let the parent build & navigate to /oauth2/authorize.
      // Native flows (PLATFORM !== "web") fall through to the redirect path.
      if (isWalletWebIframe()) {
        window.parent.postMessage(
          JSON.stringify({
            idp_after_type: after,
            idp_id: idp,
            idp_last_view_name: last_view,
          }),
          getPostMessageTargetOrigin()
        );
        resolve(null);
        return;
      }

      const state = generateRandomString(32);
      const codeVerifier = generateRandomString(64);
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const nonce = generateRandomString(32);
      const baseUrl = getAuthBaseUrl();

      // Web with no parent frame: act as wallet-webui would on receiving the
      // postMessage, so its /idp-callback can finish the flow. Storage keys,
      // redirect_uri, client_id and scope must match wallet-webui's
      // IDPConnectService.initiateLogin / LoginToIdpCallback and
      // OAuthUtils.generateOAuthUrl. Requires iaa and the wallet on one origin.
      if (window.PLATFORM === "web") {
        const walletRedirectUri = `${window.location.origin}${getWalletBasePath()}idp-callback`;
        const walletClientId =
          window.config.wallet_client_id || window.config.client_id;

        BrowserStorage.set("idp_after_type", after);
        BrowserStorage.set("idp_id", idp);
        BrowserStorage.set("idp_last_view_name", last_view);
        SessionStorage.set("oauthState", state);
        SessionStorage.set("codeVerifier", codeVerifier);

        resolve(
          `${baseUrl}/oauth2/authorize?` +
            `client_id=${encodeURIComponent(walletClientId)}` +
            `&response_type=code` +
            `&redirect_uri=${encodeURIComponent(walletRedirectUri)}` +
            `&state=${encodeURIComponent(state)}` +
            `&nonce=${encodeURIComponent(nonce)}` +
            `&scope=${encodeURIComponent(`idp ${idp}`)}` +
            `&kc_idp_hint=${encodeURIComponent(idp)}` +
            `&code_challenge_method=S256` +
            `&code_challenge=${encodeURIComponent(codeChallenge)}`
        );
        return;
      }

      const redirectUri = encodeURIComponent(
        `${window.location.origin}/oauth-success.html`
      );

      const clientId = window.config.client_id;
      let scopes = window.config.scopes
        ? window.config.scopes.join(" ")
        : "openid";
      if (scopes === "idp") {
        scopes = `idp ${idp}`;
      }
      const encodedScopes = encodeURIComponent(scopes);

      let authUrl =
        `${baseUrl}/oauth2/authorize?` +
        `client_id=${clientId}&response_type=code` +
        `&redirect_uri=${redirectUri}` +
        `&scope=${encodedScopes}` +
        `&state=${state}` +
        `&nonce=${nonce}` +
        `&code_challenge=${codeChallenge}` +
        `&code_challenge_method=S256` +
        `&kc_idp_hint=${idp}`;

      authUrl += `&referrer=${window.THEME}-${window.PLATFORM}`;

      BrowserStorage.set("idp_after_type", after);
      BrowserStorage.set("idp_id", idp);
      BrowserStorage.set("idp_last_view_name", last_view);
      SessionStorage.set("code_verifier", codeVerifier);
      SessionStorage.set("oauth_state", state);

      resolve(authUrl);
    } catch (error) {
      reject(error);
    }
  });
};

IDPConnectService.setLearnMore = function (params) {
  return SessionStorage.set("learnMore", params);
};

IDPConnectService.getLearnMore = function () {
  return SessionStorage.get("learnMore");
};

export default IDPConnectService;
